import React, { useState, useEffect, useRef } from "react";
import { Viewer, Worker } from '@react-pdf-viewer/core';
import { printPlugin, RenderPrintProps } from '@react-pdf-viewer/print';
import { zoomPlugin, RenderZoomInProps, RenderCurrentScaleProps } from '@react-pdf-viewer/zoom'
import { fullScreenPlugin, RenderEnterFullScreenProps } from '@react-pdf-viewer/full-screen';
import { Ripple } from "primereact/ripple";
import { Dialog } from "primereact/dialog";
import { Toast } from 'primereact/toast';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/print/lib/styles/index.css';

import packageJson from '../../../../../../../package.json';

interface DocumentDownloadProps {
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    fileUrl?: string;
    documentNumber?: number;
    documentCreatedDate?: string;
}

const DocumentDownload: React.FC<DocumentDownloadProps> = ({ isOpen, onClose,title, documentNumber, documentCreatedDate, fileUrl }) => {
    const toast = useRef<Toast>(null);
    const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
    const printPluginInstance = printPlugin();
    const { Print } = printPluginInstance;

    const zoomPluginInstance = zoomPlugin();
    const { CurrentScale, ZoomIn, ZoomOut } = zoomPluginInstance;

    const fullScreenPluginInstance = fullScreenPlugin();
    const { EnterFullScreen } = fullScreenPluginInstance;

    const pdfjsVersion = packageJson.dependencies['pdfjs-dist'].replace('^', '');
    const getCurrentDate = () => {
        const today = new Date();
        const day = String(today.getDate()).padStart(2, '0');
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const year = today.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const formatDate = (dateString: string | undefined): string => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('en-US', {
            month: 'short',
            day: '2-digit',
            year: 'numeric'
        }).format(date);
    };

    const handleDownloadSowDocument = async () => {
        if (!fileUrl) {
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'File not found!',
                    life: 3000
                });
            }
            return;
        }

        try {
            const response = await fetch(fileUrl);

            if (!response.ok) {
                throw new Error('Failed to fetch the file.');
            }

            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileUrl.split('/').pop() || 'document.pdf';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);
        } catch (error) {
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'Error downloading the file',
                    life: 3000
                });
            }
        }
    }

    const downloadSowModalHeader = (
        <div className="full_view_modal_header_inner">
            <h6 className="date_text">
                DATE : {getCurrentDate()}
            </h6>
            <h5>{title}&nbsp;{documentNumber} {documentCreatedDate ? (formatDate(documentCreatedDate)) : ''}</h5>
            <div className="modal_btn_area">
                <button type="button" className="download_btn p-ripple"
                    onClick={handleDownloadSowDocument}>
                    <i className="bi bi-download"></i>
                    <Ripple />
                </button>

                <button type="button" className="close_modal_btn dark p-ripple"
                    onClick={onClose}>
                    <i className="bi bi-x-circle-fill"></i>
                    <Ripple />
                </button>
            </div>
        </div>
    )

    const printBtn = (
        <Print>
            {(props: RenderPrintProps) => (
                <button className="is_btn document_tool_option p-ripple"
                    onClick={props?.onClick}>
                    Print
                    <i className="bi bi-printer"></i>
                    <Ripple />
                </button>
            )}
        </Print>
    )

    const zoomBtnGroup = (
        <div className="document_tool_option not_btn">
            Zoom
            <span className="zoom_btn_group">
                <ZoomIn>
                    {(props: RenderZoomInProps) => (
                        <button className="is_btn zoom_btn p-ripple"
                            onClick={props?.onClick}>
                            <i className="bi bi-zoom-in"></i>
                            <Ripple />
                        </button>
                    )}
                </ZoomIn>

                <span>
                    <CurrentScale>{(props: RenderCurrentScaleProps) => <React.Fragment>{`${Math.round(props.scale * 100)}%`}</React.Fragment>}</CurrentScale>
                </span>

                <ZoomOut>
                    {(props: RenderZoomInProps) => (
                        <button className="is_btn zoom_btn p-ripple"
                            onClick={props?.onClick}>
                            <i className="bi bi-zoom-out"></i>
                            <Ripple />
                        </button>
                    )}
                </ZoomOut>
            </span>
        </div>
    )

    const fullScreenBtn = (
        <EnterFullScreen>
            {(props: RenderEnterFullScreenProps) => (
                <button className="is_btn document_tool_option p-ripple"
                    onClick={props?.onClick}>
                    Full screen
                    <i className='bi bi-fullscreen'></i>
                    <Ripple />
                </button>
            )}
        </EnterFullScreen>
    )

    return (
        <React.Fragment>
            <Toast ref={toast} />

            <Dialog header={downloadSowModalHeader} headerClassName="full_view_modal_header" draggable={false}
                visible={isOpen} className="custom_modal_dialog modal_dialog_full" onHide={onClose} contentClassName="pt-2 pt-sm-2">
                <div className="custom_modal_body container-md p-0">
                    <article className="document_view_area d-flex">
                        {fileUrl ? (
                            <Worker workerUrl={`https://unpkg.com/pdfjs-dist@${pdfjsVersion}/build/pdf.worker.min.js`}>
                                <Viewer fileUrl={fileUrl} plugins={[printPluginInstance, zoomPluginInstance, fullScreenPluginInstance]} />
                            </Worker>
                        ) : (
                            <div className="no_document_available_area">
                                <img src="/assets/images/no_data/no_document.png" className="p-sm-4" alt="No document available." />
                                <p>No document available.</p>
                            </div>
                        )}
                    </article>
                </div>
            </Dialog>

            {(isOpen && fileUrl !== '') && (
                <div className="document_tool_options_area">
                    {zoomBtnGroup}
                    {printBtn}
                    {fullScreenBtn}
                </div>
            )}
        </React.Fragment>
    )
}

export default DocumentDownload;